import { GeneralContentSchemaType, GeneralContentsContentType } from "~/lib/schemas/generalContentSchema.schema"
import { fetchChatContentsAction } from "~/lib/server/general-contents/fetchChatContentsAction";
import React, { useEffect, useMemo, useState } from "react";
import { If } from "@kit/ui/if";
import { ProductComponent } from "./product-component";
import { Skeleton } from "@kit/ui/skeleton";
import { ContentDetailDrawer } from "./content-detail-drawer";
import { isNeedToShowDetailProduct } from "~/lib/utils";

interface ContentCatalogProps {
  accountId: string;
  clientId: string;
  contentType: GeneralContentsContentType
}

export function ContentCatalog({ clientId, contentType }: ContentCatalogProps) {
  const [contents, setContents] = React.useState<GeneralContentSchemaType[]>([]);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [error, setError] = React.useState<boolean>(false);
  const [frameWidth, setFrameWidth] = useState(window.innerWidth);

  React.useEffect(() => {
    const fetchData = async () => {
      setLoading(true)
      const response = await fetchChatContentsAction({ clientId, contentType, page: 1, limit: 999 });
      setLoading(false)
      if (response.error) {
        setError(true)
      } else {
        setError(false)
        setContents(response.data?.data ?? []);
      }
    };

    fetchData();
  }, [clientId, contentType]);

  useEffect(() => {
    const handleResize = () => {
      setFrameWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const itemWidth = useMemo(() => {
    if (frameWidth < 640) { // Mobile
      return `${100/2}%`;
    } else if (frameWidth < 768) { // Tablet
      return `${100/3}%`;
    } else { // Desktop
      return `${100/4}%`;
    }
  }, [frameWidth]);

  const paddingValue = useMemo(() => {
    if (frameWidth < 640) { // Mobile
      return 1;
    } else if (frameWidth < 768) { // Tablet
      return 2;
    } else { // Desktop
      return 3;
    }
  }, [frameWidth]);

  return (
    <div className="flex flex-wrap container max-w-4xl">
      <If condition={error}>
        <div className="w-full text-center">Something went wrong, please try again.</div>
      </If>
      <If condition={!loading && !error && contents.length === 0}>
        <div className="w-full text-center">Empty.</div>
      </If>
      <If condition={loading}>
        {[...Array(5)].map((_, index) => (
          <div key={index} className={`p-${paddingValue}`} style={{ width: itemWidth }}>
            <Skeleton className="aspect-square" />
          </div>
        ))}
      </If>
      { contents?.map(item => {
        if (isNeedToShowDetailProduct(item.action_titles, item.action_urls) && item.content_type !== 'link') {
          return (
            <div key={item.id} className={`p-${paddingValue}`} style={{ width: itemWidth }}>
              <ContentDetailDrawer
                content={{
                  ...item,
                  created_at: item.created_at ?? '',
                  updated_at: item.updated_at ?? '',
                  currency: item.currency ?? '',
                  id: item.id ?? '',
                  title: item.title ?? '',
                  description: item.description ?? '',
                  price: item.price ?? null,
                  thumbnail_url: item.thumbnail_url ?? null,
                  image_urls: item.image_urls ?? null,
                }}
              >
                <ProductComponent key={item.id} data={item} source='catalog' onClick={undefined}/>
              </ContentDetailDrawer>
            </div>
          )
        } else {
          return (
            <div key={item.id} className={`p-${paddingValue}`} style={{ width: itemWidth }}>
              <ProductComponent key={item.id} data={item} source='catalog' />
            </div>
          )
        }
      })}
    </div>
  )
}